import { Injectable, Injector } from '@angular/core';

import { FormFileUploaderComponent } from '@shared/components';
import { QuestionPage } from './../../pages/question/question.page';
import { IProperty, PropertySource, PropertySubject } from '@services/property/property-model.interfaces';
import { FormFileUploaderConfig } from '@shared/components/form-file-uploader/model';
import { ContentListComponent } from '@modules/management/pages/details/content/components';
import { QuestionType } from '@modules/management/pages/details/check/services/question/model';
import { PropertyPickerComponent } from '@modules/management/modules/property-picker/components';
import {
  ListColumn,
  PropertyPickerConfig
} from '@modules/management/modules/property-picker/model/property-picker.interfaces';
import {
  DropdownSelectionModel,
  IncrementEntryFieldsModel,
  IQuestion,
  MeasurementFieldsModel,
  MultipleChoiceFieldsModel,
  NumberInputFieldsModel,
  PassFailFieldsModel,
  PropertyFieldsModel,
  TextInputFieldsModel,
  USER_FILTER_FIELDS,
  USER_QUESTION_TYPES,
  UserFieldsModel
} from '@modules/management/pages/details/check/models';
import { buildUserFilter, IUserFilter, keepUnofferedIds } from '@services/accounts/user-filter';
import {
  ChecksService,
  DynamicComponentCreatorService,
  FormBuilderService,
  FormField,
  Module,
  ObjectItem,
  SettingsService,
  SubscriberService,
  UtilsService
} from '@services';

import { TranslateService } from '@ngx-translate/core';
import {
  each,
  filter,
  find,
  flatten,
  get,
  has,
  includes,
  intersection,
  isArray,
  isNull,
  isUndefined,
  keys,
  map,
  mapValues,
  omit,
  orderBy,
  set,
  values
} from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class QuestionFormService {
  public questionPage: QuestionPage;
  public sharedFields = {
    issueType: {
      containerClass: 'question-field',
      name: 'issueType',
      type: 'radio',
      default: 0,
      options: [
        {
          id: 0,
          description: this.translate.instant('MGMT_DETAILS.Allow_CA_Comment')
        },
        {
          id: 1,
          description: this.translate.instant('MGMT_DETAILS.Require_CA')
        },
        {
          id: 2,
          description: this.translate.instant('MGMT_DETAILS.Require_Comment')
        }
      ]
    },
    attachCommentOption: {
      containerClass: 'question-field',
      type: 'flipswitch',
      name: 'issueTypeFail_Comment',
      title: 'MGMT_DETAILS.Attach_Comment',
      onText: this.translate.instant('SHARED.On'),
      offText: this.translate.instant('SHARED.Off'),
      default: 0,
      value: 1
    },
    correctiveOption: {
      containerClass: 'question-field',
      type: 'flipswitch',
      name: 'issueTypeFail_Corrective',
      title: 'MGMT_DETAILS.Log_n_Corrective',
      onText: this.translate.instant('SHARED.On'),
      offText: this.translate.instant('SHARED.Off'),
      default: 0,
      value: 1
    },
  };
  private passFailFieldsModel: PassFailFieldsModel;
  private multipleChoiceFieldsModel: MultipleChoiceFieldsModel;
  private dropdownSelectionModel: DropdownSelectionModel;
  private textInputFieldsModel: TextInputFieldsModel;
  private propertyFieldsModel: PropertyFieldsModel;
  private numberInputFieldsModel: NumberInputFieldsModel;
  private incrementEntryFieldsModel: IncrementEntryFieldsModel;
  private measurementFieldsModel: MeasurementFieldsModel;
  private userFieldsModel: UserFieldsModel;
  private mainFields: any[] = [
    {
      containerClass: 'question-field common-label-field',
      required: true,
      inputtype: 'verbatim',
      name: 'title',
      title: 'MGMT_DETAILS.Question_Item',
      type: 'text',
      size: 20
    },
    {
      containerClass: 'question-field common-label-field',
      inputtype: 'verbatim',
      name: 'translation_title',
      title: this.translate.instant('MGMT_DETAILS.Question_Item_Translation'),
      type: 'textTranslations',
      fromID: 'translations',
      size: 20,
    },
    {
      containerClass: 'question-field question-type question-type-selector',
      title: 'MGMT_DETAILS.Question_Item_Type',
      type: 'subtitle'
    },
    {
      containerClass: 'question-field question-type question-type-selector',
      title: '',
      name: 'type',
      type: 'radio',
      break: true,
      func: (type: any) => {
        const extension = includes([QuestionType.MultipleChoice, QuestionType.IncrementEntry, QuestionType.DropdownSelection], type.id) ? 'png' : 'svg';
        let id = type.id;

        if (id === QuestionType.Property && !this.getPropertySubject()) {
          return;
        }

        if (includes([QuestionType.TextInput, QuestionType.NumberInput, QuestionType.Property], id)) {
          id = QuestionType.TextInput;
        }

        return `
          <div class="type-image">
            <img src="assets/images/questionTypes/${id}.${extension}">
          </div>
          <div class="question-type-title">${type.description}</div>
        `;
      },
      options: [
        {
          id: QuestionType.PassFail,
          description: this.translate.instant('MGMT_DETAILS.Pass_Fail')
        },
        {
          id: QuestionType.MultipleChoice,
          description: this.translate.instant('MGMT_DETAILS.Multiple_Choice')
        },
        {
          id: QuestionType.TextInput,
          description: this.translate.instant('MGMT_DETAILS.Text_Input')
        },
        {
          id: QuestionType.NumberInput,
          description: this.translate.instant('MGMT_DETAILS.Number_Input')
        },
        {
          id: QuestionType.Property,
          description: this.translate.instant('PROPERTY.Property_value')
        },
        {
          id: QuestionType.DropdownSelection,
          description: this.translate.instant('MGMT_DETAILS.Dropdown_Selection')
        },
        {
          id: QuestionType.IncrementEntry,
          description: this.translate.instant('MGMT_DETAILS.Increment_Entry')
        },
        {
          id: QuestionType.Measurement,
          description: this.translate.instant('MGMT_DETAILS.Tolerance')
        },
        {
          id: QuestionType.User,
          description: this.translate.instant('MGMT_DETAILS.User_Selection')
        }
      ],
      onChange: (value: string) => {
        this.questionPage.currentGroupType = value;
        this.showFieldsByClass(value);
        this.checkFormControlsVisibility();
        this.checkTypesAvailability();
      }
    }
  ];
  private commonFields = [
    {
      containerClass: 'question-field question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection',
      title: this.translate.instant('MGMT_DETAILS.Allow_NA'),
      name: 'allowNA',
      type: 'flipswitch',
      onText: this.translate.instant('SHARED.On'),
      offText: this.translate.instant('SHARED.Off'),
      value: 1,
      default: 0
    },
    {
      type: 'divider',
      containerClass: 'question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection'
    },
    {
      title: this.translate.instant('MGMT_DETAILS.Question_Categories'),
      type: 'subtitle',
      containerClass: 'question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection'
    },
    {
      name: 'closingCategory',
      title: this.translate.instant('SHARED.Category'),
      type: 'selectmenu',
      multiple: false,
      searchable: true,
      canClear: true,
      placeholder: this.translate.instant('SHARED.None'),
      containerClass: 'question-field question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection',
      options: []
    },
    {
      title: this.translate.instant('DASHPAGES.CLOSE_OBSERVATION_Tags'),
      type: 'subtitle',
      containerClass: 'question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection'
    },
    {
      name: 'closingTags',
      type: 'selectmenu',
      multiple: true,
      searchable: false,
      canClear: false,
      tags: true,
      placeholder: this.translate.instant('SHARED.Add_Tags'),
      default: null,
      containerClass: 'full-width question-field question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection',
      options: this.settingsService.customTags.data,
      valueProperty: 'tagID',
      func: (item: any) => item.tag
    },
    {
      type: 'divider',
      containerClass: 'question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection'
    },
    {
      title: 'MGMT_DETAILS.Reference_Image',
      type: 'subtitle',
      containerClass: 'question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection'
    },
    {
      containerClass: 'question-field question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection',
      title: 'MGMT_DETAILS.Include_Reference_Image',
      name: 'showImage',
      type: 'flipswitch',
      onText: this.translate.instant('SHARED.On'),
      offText: this.translate.instant('SHARED.Off'),
      value: 1,
      default: 0,
      onChange: (value) => this.checkReferenceImageFieldAvailability(value)
    },
    {
      containerClass: 'custom-form-element question-field question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection',
      type: 'customElement',
      title: 'MGMT_DETAILS.Reference_Image',
      name: 'images',
      component: FormFileUploaderComponent,
      inputs: {
        config: <FormFileUploaderConfig>{
          metadata: {
            type: 'image',
            subtype: 'question'
          }
        }
      }
    },
    {
      type: 'divider',
      containerClass: 'question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection'
    },
    {
      title: 'MGMT_DETAILS.Smart_Check_Details',
      type: 'subtitle',
      containerClass: 'question-type-field passFail multipleChoice'
    },
    {
      containerClass: 'question-field question-type-field passFail multipleChoice',
      title: 'MGMT_DETAILS.Question_Item_Importance',
      name: 'importance',
      type: 'range',
      break: true,
      showTicks: true,
      showTicksValues: true,
      max: 5,
      min: 1,
      default: 1,
      hideInput: true,
      getLegend: (value: number): string => {
        let postfix = '';

        if (value === 1) {
          postfix = ` - ${this.translate.instant('SHARED.Low')}`;
        }

        if (value === 5) {
          postfix = ` - ${this.translate.instant('SHARED.High')}`;
        }

        return `${value.toString()} ${postfix}`;
      }
    },
    {
      type: 'divider',
      containerClass: 'question-type-field passFail multipleChoice'
    },
    {
      title: 'MGMT_DETAILS.Question_Item_Help',
      type: 'subtitle',
      containerClass: 'question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection'
    },
    {
      containerClass: 'question-field question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection',
      title: 'MGMT_DETAILS.Enable_Help_Content',
      name: 'helpAvailable',
      type: 'flipswitch',
      onText: this.translate.instant('SHARED.On'),
      offText: this.translate.instant('SHARED.Off'),
      value: 1,
      default: 0,
      onChange: (value) => this.checkHelpFieldAvailability(value)
    },
    {
      containerClass: 'question-field question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection',
      title: 'MGMT_DETAILS.Help_Hint_Text',
      name: 'helpContent',
      inputtype: 'verbatim',
      type: 'text'
    },
    {
      containerClass: 'question-field question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection',
      title: this.translate.instant('MGMT_DETAILS.Help_Hint_Text_Translation'),
      name: 'translation_helpContent',
      type: 'textTranslations',
      inputtype: 'verbatim'
    },
    {
      title: 'MGMT_DETAILS.Check_Content',
      type: 'subtitle',
      containerClass: 'question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection'
    },
    {
      containerClass: 'question-field question-type-field passFail multipleChoice textInput numberInput property user incrementEntry measurement dropdownSelection',
      type: 'customElement',
      name: 'selectedContent',
      component: ContentListComponent,
      module: Module.CONTENT
    }
  ];

  constructor(
    public translate: TranslateService,
    public dynamicComponentCreatorService: DynamicComponentCreatorService,
    private utils: UtilsService,
    private formBuilderService: FormBuilderService,
    private settingsService: SettingsService,
    private subscriber: SubscriberService,
    private checkService: ChecksService,
    protected injector: Injector
  ) {}

  public getFields(questionPage: QuestionPage): any {
    this.questionPage = questionPage;
    this.checkTypeFieldVisibility();
    this.defineQuestionCategories();

    this.passFailFieldsModel = new PassFailFieldsModel(this, this.translate);
    this.multipleChoiceFieldsModel = new MultipleChoiceFieldsModel(this, this.translate);
    this.textInputFieldsModel = new TextInputFieldsModel(this, this.translate);
    this.propertyFieldsModel = new PropertyFieldsModel(this, this.injector);
    this.numberInputFieldsModel = new NumberInputFieldsModel(this, this.translate);
    this.dropdownSelectionModel = new DropdownSelectionModel(this, this.translate);
    this.incrementEntryFieldsModel = new IncrementEntryFieldsModel(this, this.translate, this.formBuilderService);
    this.measurementFieldsModel = new MeasurementFieldsModel(this, this.translate);
    this.userFieldsModel = new UserFieldsModel(this, this.injector);

    const modelsFields: any[] = [
      this.passFailFieldsModel.getFields(),
      this.multipleChoiceFieldsModel.getFields(),
      this.textInputFieldsModel.getFields(),
      this.propertyFieldsModel.getFields(),
      this.numberInputFieldsModel.getFields(),
      this.dropdownSelectionModel.getFields(),
      this.incrementEntryFieldsModel.getFields(),
      this.measurementFieldsModel.getFields(),
      this.userFieldsModel.getFields()
    ];

    const labelFields = map(modelsFields, (field) => field.shift());

    return filter([
      ...labelFields,
      ...this.mainFields,
      ...flatten(modelsFields),
      ...this.commonFields,
      this.getPropertyField()
    ]);
  }

  public showFieldsByClass(className: string): void {
    const baseFieldClass = 'question-type-field';
    let fieldSelector = `#${this.questionPage.formId} .${baseFieldClass}`;
    this.setFieldsVisibility(fieldSelector, false);

    if (className) {
      fieldSelector += `.${className}`;
      this.setFieldsVisibility(fieldSelector, true);
    }

    this.passFailFieldsModel.showGroupList();
    this.multipleChoiceFieldsModel.showGroupList();
    this.textInputFieldsModel.showGroupList();
    this.numberInputFieldsModel.showGroupList();
    this.dropdownSelectionModel.showGroupList();
    this.incrementEntryFieldsModel.showGroupList();
    this.measurementFieldsModel.showGroupList();

    if (className === QuestionType.User) {
      this.userFieldsModel.updateMatchCount();
    }

    this.checkCommonFieldsAvailability();
    this.checkTypesAvailability();
    this.prepareSettings();
  }

  public onDestroy() {
    this.passFailFieldsModel.onDestroy();
    this.multipleChoiceFieldsModel.onDestroy();
    this.textInputFieldsModel.onDestroy();
    this.numberInputFieldsModel.onDestroy();
    this.dropdownSelectionModel.onDestroy();
    this.incrementEntryFieldsModel.onDestroy();
    this.measurementFieldsModel.onDestroy();
  }

  public isValid(): boolean {
    if (this.questionPage.currentGroupType === QuestionType.PassFail) {
      return this.passFailFieldsModel.isValid();
    } else if (this.questionPage.currentGroupType === QuestionType.MultipleChoice) {
      return this.multipleChoiceFieldsModel.isValid();
    } else if (this.questionPage.currentGroupType === QuestionType.TextInput) {
      return this.textInputFieldsModel.isValid();
    } else if (this.questionPage.currentGroupType === QuestionType.Property) {
      return this.propertyFieldsModel.isValid();
    } else if (this.questionPage.currentGroupType === QuestionType.NumberInput) {
      return this.numberInputFieldsModel.isValid();
    } else if (this.questionPage.currentGroupType === QuestionType.Measurement) {
      return this.measurementFieldsModel.isValid();
    } else if (this.questionPage.currentGroupType === QuestionType.DropdownSelection) {
      return this.dropdownSelectionModel.isValid();
    } else if (this.questionPage.currentGroupType === QuestionType.IncrementEntry) {
      return this.incrementEntryFieldsModel.isValid();
    }

    return true;
  }

  public setFieldVisibility(field: HTMLElement | HTMLInputElement, isVisible: boolean): void {
    if (field) {
      field.hidden = !isVisible;
      (<HTMLInputElement>field).disabled = !isVisible;

      const questionField: HTMLElement = (<HTMLElement>field.closest('.question-field'));

      if (questionField) {
        questionField.hidden = !isVisible;
        this.disableFieldByElement('input', questionField, !isVisible);
        this.disableFieldByElement('select', questionField, !isVisible);
      }
    }
  }

  public disableFieldByElement(fieldType: string, element: HTMLElement, value: boolean) {
    const targetElement: HTMLInputElement = this.getElementBy(fieldType, element);

    if (targetElement) {
      targetElement.disabled = value;
    }
  }

  public checkFormControlsVisibility(): void {
    const formControlsElement = (<any>this.questionPage).elementRef.nativeElement.querySelector('.ui-form-controls.ui-grid-a');
    formControlsElement.hidden = !this.questionPage.currentGroupType;
  }

  public getFormData(formData: any): any {
    this.encodeTranslations(formData);
    this.getImpactField(formData);
    this.getDataByGroupType(formData);
    this.prepareCategories(formData);
    this.prepareTags(formData);

    if (has(formData, 'showImage')) {
      formData.showImage = formData.showImage || 0;
    }

    if (has(formData, 'helpAvailable')) {
      formData.helpAvailable = formData.helpAvailable || 0;
    }

    if (has(formData, 'allowNA')) {
      formData.allowNA = formData.allowNA || 0;
    }

    if (formData.images) {
      try {
        const images = flatten([JSON.parse(formData.images)]);
        formData.images = filter(map(images, (image: number | ObjectItem) => {
          return (image as ObjectItem)?.objectID ?? image;
        }));
      } catch (e) {}
    }

    if (has(formData, 'selectedProperties')) {
      try {
        formData.selectedProperties = JSON.parse(formData.selectedProperties);
      } catch (e) {}
    }

    if (has(formData, 'selectedProperty')) {
      try {
        formData.selectedProperties = JSON.parse(formData.selectedProperty);
        delete formData.selectedProperty;

        if ('healthRelated' in formData) {
          formData.selectedProperties[0].healthRelated = formData.healthRelated;
        }
      } catch (e) {}
    }
  }

  public getElementBy(selector: string, targetElement?: any): any {
    const nativeElement = targetElement || (<any>this.questionPage).elementRef.nativeElement;
    return nativeElement.querySelector(selector);
  }

  public syncRequiredFields(elementRef, fields: any, requiredOptionFieldName: string) {
    each(fields, (value: string, fieldName: string) => {
      const requiredFieldSelector = `input[name="required_${fieldName}"]`;
      const requiredField: HTMLInputElement = this.getElementBy(requiredFieldSelector, elementRef.nativeElement);

      if (requiredField) {
        if (value === null) {
          value = this.getInputFieldValueByName(fieldName, elementRef.nativeElement);
        }
        requiredField.disabled = !value;
        (<HTMLElement>requiredField.closest('.question-field')).hidden = !value;
      }
    });

    this.checkRequiredField(elementRef.nativeElement, fields, requiredOptionFieldName);
  }

  public checkRequiredField(element: HTMLElement, targetFields: any, targetFieldName: string, requiredValues: any = {}) {
    each(targetFields, (value: string, fieldName: string) => {
      if (isUndefined(value) || isNull(value)) {
        value = this.getInputFieldValueByName(fieldName, element);
      }

      targetFields[fieldName] = value;

      if (!get(requiredValues, `required_${fieldName}`)) {
        const fieldValue = values(this.getInputFieldValueByName(`required_${fieldName}`, element));
        set(requiredValues, `required_${fieldName}`, fieldValue);
      }
    });
    requiredValues = filter(flatten(values(requiredValues)));

    const fieldsValues: any[] = values(targetFields);
    const isEnabled: boolean = filter(fieldsValues).length === fieldsValues.length && !requiredValues.length;
    const fieldSelector = `input[name="${targetFieldName}"]`;
    const requiredField: HTMLInputElement = this.getElementBy(fieldSelector, element);

    if (requiredField) {
      const dividerField: HTMLElement = this.getElementBy(`.${targetFieldName}_divider`, element);

      requiredField.disabled = !isEnabled;
      (<HTMLElement>requiredField.closest('.question-field')).hidden = !isEnabled;
      if (dividerField) {
        dividerField.hidden = !isEnabled;
      }
    }
  }

  public failChoiceValidation(element: HTMLElement, targetFieldName: string, value: boolean, formData: any = {}) {
    if (value) {
      const targetElement: HTMLInputElement = this.getElementBy(`[name="${targetFieldName}"]`, element);
      targetElement.checked = false;
      this.markFieldAsChanged(targetFieldName, '', formData);
    }
    this.formBuilderService.disableFieldByName(element, targetFieldName, value);
  }

  public markFieldAsChanged(fieldName: string, value: any = '', formData: any = {}) {
    let originalValue: any = formData[fieldName] || '';

    if (isArray(value) && !isArray(originalValue)) {
      originalValue = originalValue ? [originalValue.toString()] : [];
    }

    if (this.questionPage.questionId) {
      this.formBuilderService.markFieldAsChanged(`#questionEditForm`, fieldName, originalValue, value);
    }
  }

  public isModelAvailable(model: string): boolean {
    return this.questionPage.questionId && this.questionPage.currentGroupType === model || !this.questionPage.questionId;
  }

  public prepareQuestionType(question: IQuestion) {
    if (includes(['singleSelect', 'multiSelect'], question.type)) {
      (question as any).selectionType = question.type;
      question.type = QuestionType.MultipleChoice;
    }

    if (includes(['tolerance', 'actualMeasurement'], question.type)) {
      (question as any).measurementType = question.type;
      question.type = QuestionType.Measurement;
    }

    if (question.type === QuestionType.Property) {
      question.selectedProperty = question.selectedProperties;
      (question as any).healthRelated = +question.selectedProperty?.[0]?.healthRelated;
    }

    if (includes(USER_QUESTION_TYPES, question.type)) {
      (question as any).userSelectionType = question.type;
      question.type = QuestionType.User;
      each(USER_FILTER_FIELDS, (fieldName: string, key: string) => {
        (question as any)[fieldName] = get(question.userFilter, key, []);
      });
    }
  }

  /**
   * The userFilter the form currently describes, given a reader for each filter field's value.
   * Stored IDs the editor was not offered as options are kept (see keepUnofferedIds).
   */
  public getUserFilter(readField: (fieldName: string) => any): IUserFilter {
    const stored: IUserFilter = get(this.checkService.questions, [this.questionPage.questionId, 'userFilter']) || {};
    const prefix: string = this.questionPage.formConfig.prefix;

    return buildUserFilter(mapValues(USER_FILTER_FIELDS, (fieldName: string, key: string) => {
      const offered: string[] = map($(`#${prefix}${fieldName} option`), (option: HTMLOptionElement) => option.value);
      return keepUnofferedIds(readField(fieldName), stored[key], offered);
    }));
  }

  public validFormGroups(formGroups): boolean {
    const formIds: string[] = map(formGroups, (group) => `${group.formConfig.prefix}Form`);

    if (formIds.length) {
      const formValidResults: boolean[] = filter(map(formIds, (formId: string) => (<any>$(`#${formId}`)).valid()));
      return formValidResults.length === formIds.length;
    } else {
      return true;
    }
  }

  private setFieldsVisibility(fieldSelector: string, isVisible: boolean) {
    const fields: HTMLElement[] = (<any>this.questionPage).elementRef.nativeElement.querySelectorAll(fieldSelector);
    each(fields, (field) => this.setFieldVisibility(field, isVisible));
  }

  private prepareCategories(formData): void {
    formData.closingCategory = formData.closingCategory ? formData.closingCategory : 'none:0';
  }

  private prepareTags(formData): void {
    formData.closingTags = JSON.stringify(filter(map(flatten([formData.closingTags]), Number)));
  }

  private getDataByGroupType(formData): void {
    const currentModel = this.getCurrentModel();

    if (currentModel && currentModel.getData) {
      formData.questionOptions = currentModel.getData();
    }

    if (this.questionPage.currentGroupType === 'multipleChoice') {
      formData.type = formData.selectionType;
      delete formData.selectionType;
    }

    if (this.questionPage.currentGroupType === QuestionType.Measurement) {
      formData.type = 'tolerance';
      delete formData.measurementType;
    }

    if (this.questionPage.currentGroupType === QuestionType.User) {
      formData.type = formData.userSelectionType || 'singleUser';
      formData.userFilter = JSON.stringify(this.getUserFilter((fieldName: string) => formData[fieldName]));
      delete formData.userSelectionType;
      each(USER_FILTER_FIELDS, (fieldName: string) => delete formData[fieldName]);
    }
  }

  private encodeTranslations(formData): void {
    this.utils.encodeTranslations(formData, ['translation_title', 'translation_helpContent'], ['title', 'explanation']);
  }

  private getImpactField(formData): void {
    const impactField: any = find(this.commonFields, <any>{name: 'importance'});

    if (impactField && includes([QuestionType.PassFail, QuestionType.MultipleChoice], this.questionPage.currentGroupType)) {
      formData[impactField.name] = impactField.getValue();
    }
  }

  private checkCommonFieldsAvailability() {
    this.checkReferenceImageFieldAvailability();
    this.checkHelpFieldAvailability();
  }

  private checkReferenceImageFieldAvailability(value?: any) {
    const includeReferenceImageValue = isUndefined(value) ? this.getInputFieldValueByName('showImage') : value;
    this.setFieldVisibility(this.getElementBy(`#${this.questionPage.formConfig.prefix}reference_image-container`), includeReferenceImageValue);
  }

  private checkHelpFieldAvailability(value?: any) {
    const enableHelpContentValue = isUndefined(value) ? this.getInputFieldValueByName('helpContent') : value;
    this.setFieldVisibility(this.getElementBy(`input[name="helpContent"]`), enableHelpContentValue);
    this.setFieldVisibility(this.getElementBy(`#translation_helpContentcollapsibleItem`), enableHelpContentValue);
  }

  private getInputFieldValueByName(name: string, targetElement?: any): any {
    const fieldElement: HTMLInputElement = this.getElementBy(`input[name="${name}"]`, targetElement);
    return get(fieldElement, 'value');
  }

  private checkTypeFieldVisibility() {
    const typeField: any = find(this.mainFields, <any>{name: 'type'});
    typeField.disabled = (<any>this.questionPage).route.snapshot.paramMap.get('id');
  }

  private checkTypesAvailability() {
    if (this.questionPage.currentGroupType) {
      this.setFieldsVisibility(`.question-type-selector`, false);
      this.setFieldsVisibility(`.common-label-field`, true);
    } else {
      this.setFieldsVisibility(`.common-label-field`, false);
    }
  }

  private prepareSettings() {
    each(this.questionPage.questionOptions, (questionOption) => {
      const settings = get(questionOption, 'settings') || {};

      if (includes([QuestionType.PassFail, QuestionType.MultipleChoice], this.questionPage.currentGroupType)) {
        if (settings.usesNote) {
          settings.notes_validation = true;

          if (settings.usesNote === 2) {
            settings.required_notes_validation = true;
          }
        }

        if (settings.usesPhoto) {
          settings.photo_validation = true;

          if (settings.usesPhoto === 2) {
            settings.required_photo_validation = true;
          }
        }

        if (settings.verificationRequired) {
          settings.required_validation_field = true;
        }

        if (settings.verificationRequired) {
          settings.required_validation_field = true;
        }

      }

      if (includes([QuestionType.TextInput, QuestionType.NumberInput], this.questionPage.currentGroupType)) {
        const textInputFields: string[] = ['canRepeat', 'usesBarcode', 'required'];
        if (intersection(keys(settings), textInputFields).length) {
          settings.fieldOptions = [];

          if (settings.required) {
            settings.fieldOptions.push('required');
          }

          if (settings.canRepeat) {
            settings.fieldOptions.push('multiple');
          }

          if (settings.usesBarcode) {
            settings.fieldOptions.push('barcode');
          }
        }
      }

      if (this.questionPage.currentGroupType === QuestionType.Measurement) {
        if (settings.usesNote) {
          settings.fail_notes = true;

          if (settings.usesNote === 2) {
            settings.required_fail_notes = true;
          }
        }

        if (settings.verificationRequired) {
          settings.required_measurement_choice_validation_field = true;
        }
      }

      if (settings.issueType) {
        settings.issueTypeFail = settings.issueType;
        if (settings.issueTypeFail === '2' || settings.issueTypeFail === '0') {
          settings.issueTypeFail_Comment = true;
        }
        if (settings.issueTypeFail === '1' || settings.issueTypeFail === '2') {
          settings.issueTypeFail_Corrective = true;
        }
      }

      questionOption.settings = omit(settings, [
        'usesNote',
        'usesPhoto',
        'verificationRequired',
        'canRepeat',
        'usesBarcode'
      ]);
    });
  }

  private defineQuestionCategories(): void {
    const categoryField = find(this.commonFields, {name: 'closingCategory'});
    const locationId = this.subscriber.locationID();
    const observationTypes: string[] = ['category', 'pi', 'quality'];
    const titleMap = {
      category: this.translate.instant('SHARED.Unsafe_Conditions'),
      pi: this.translate.instant('SHARED.Process_Improvements'),
      quality: this.translate.instant('SHARED.Quality_Issues')
    };

    if (categoryField) {
      categoryField.options = [];

      each(observationTypes, (type) => {
        const categoriesByType: any[] = orderBy(this.settingsService.getSettingSync(type, locationId, true), 'messageTitle');
        categoryField.options = [
          ...categoryField.options,
          ...map(categoriesByType, (category) => {
            const observationType: string = type === 'category' ? 'condition' : type;
            return {
              id: `${observationType}:${category.messageID}`,
              description: `${titleMap[type]} - ${category.messageTitle}`
            };
          })
        ];
      });
    }
  }

  private getCurrentModel() {
    const modelsMap = {
      passFail: this.passFailFieldsModel,
      multipleChoice: this.multipleChoiceFieldsModel,
      textInput: this.textInputFieldsModel,
      property: this.propertyFieldsModel,
      numberInput: this.numberInputFieldsModel,
      incrementEntry: this.incrementEntryFieldsModel,
      dropdownSelection: this.dropdownSelectionModel,
      measurement: this.measurementFieldsModel,
      user: this.userFieldsModel
    };
    return modelsMap[this.questionPage.currentGroupType];
  }

  private getPropertyField(): FormField {
    const subject = this.getPropertySubject();

    if (subject) {
      return {
        containerClass: 'question-field question-type-field passFail multipleChoice textInput numberInput incrementEntry measurement dropdownSelection',
        type: 'customElement',
        name: 'selectedProperties',
        component: PropertyPickerComponent,
        module: Module.PROPERTIES,
        inputs: {
          pickerConfig: <PropertyPickerConfig>{
            type: subject,
            selectedList: {
              columns: [
                ListColumn.PropertyName,
                ListColumn.Required
              ]
            },
            viewTree: {
              filter(property: IProperty) {
                return includes([PropertySource.Hybrid, PropertySource.Manual], property.source);
              },
            }
          }
        }
      };
    }
  }

  public getPropertySubject(): PropertySubject {
    const checkID = (<any>this.questionPage).route.snapshot.paramMap.get('checkId');
    return this.checkService.getPropertySubjectByCheck(checkID);
  }
}
